import { useEffect, useMemo, useState } from 'react';
import { X, ChevronDown, TrendingDown, TrendingUp, Minus, Plus, Heart, Sparkles, BarChart3, Award } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { RarityBadge } from '@/components/RarityBadge';
import { PriceChart, type ChartSeries } from '@/components/PriceChart';
import { useCardPrints, TREATMENT_LABELS, type Card, type CardPrint } from '@/hooks/useCards';
import { usePrintHistory } from '@/hooks/usePrintHistory';
import type { InkColor } from '@/lib/ink-utils';

interface Props {
  card: Card;
  owned: boolean;
  ownedQty: number;
  wishlisted: boolean;
  onClose: () => void;
  onAddToCollection: (card: Card) => void;
  onToggleWishlist: (card: Card) => void;
  isSignedIn: boolean;
}

type Tab = 'raw' | 'graded' | 'pop';
type Range = '1M' | '3M' | '6M' | '12M' | 'MAX';
const RANGES: Range[] = ['1M', '3M', '6M', '12M', 'MAX'];

const SERIES_COLORS: Record<string, string> = {
  cold_foil: 'hsl(174 70% 55%)',
  normal: 'hsl(259 70% 70%)',
  holofoil: 'hsl(289 70% 65%)',
  rainbow_foil: 'hsl(320 75% 65%)',
  enchanted: 'hsl(var(--ink-amethyst))',
  epic: 'hsl(var(--ink-ruby))',
  iconic: 'hsl(var(--primary))',
  inkwash_foil: 'hsl(var(--ink-steel))',
  glimmer_foil: 'hsl(var(--ink-sapphire))',
  iridescent: 'hsl(var(--ink-emerald))',
  promo: 'hsl(var(--ink-amber))',
};

function rangeCutoff(r: Range): number {
  const now = Date.now();
  const day = 86400000;
  switch (r) {
    case '1M': return now - 30 * day;
    case '3M': return now - 90 * day;
    case '6M': return now - 180 * day;
    case '12M': return now - 365 * day;
    case 'MAX': return 0;
  }
}

export function CardDetailSheet({
  card, owned, ownedQty, wishlisted, onClose,
  onAddToCollection, onToggleWishlist, isSignedIn,
}: Props) {
  const { data: prints = [] } = useCardPrints(card.id);
  const [tab, setTab] = useState<Tab>('raw');
  const [range, setRange] = useState<Range>('3M');
  const [selectedPrintId, setSelectedPrintId] = useState<string | null>(null);
  const [treatmentMenu, setTreatmentMenu] = useState(false);

  useEffect(() => { setSelectedPrintId(null); }, [card.id]);

  const printsWithPrice = prints.filter(p => p.price_usd != null);
  const selectedPrint: CardPrint | undefined =
    prints.find(p => p.id === selectedPrintId) ||
    printsWithPrice.find(p => p.treatment === 'cold_foil') ||
    printsWithPrice.find(p => p.treatment === 'normal') ||
    printsWithPrice[0] ||
    prints[0];

  const printIds = useMemo(() => prints.map(p => p.id), [prints]);
  const { data: historyMap } = usePrintHistory(printIds);

  // Build chart series (RAW tab): selected treatment + a comparison series (the other primary)
  const chartSeries: ChartSeries[] = useMemo(() => {
    if (!selectedPrint) return [];
    const cutoff = rangeCutoff(range);
    const make = (p: CardPrint): ChartSeries | null => {
      const hist = historyMap?.get(p.id) || [];
      const filtered = hist.filter(pt => pt.t >= cutoff);
      // Always append current live price as last point
      if (p.price_usd != null) {
        filtered.push({ t: Date.now(), price: Number(p.price_usd) });
      }
      if (filtered.length === 0) return null;
      const info = TREATMENT_LABELS[p.treatment];
      return {
        id: p.id,
        label: info?.label || p.treatment,
        color: SERIES_COLORS[p.treatment] || 'hsl(var(--primary))',
        data: filtered,
      };
    };
    const series: ChartSeries[] = [];
    const primary = make(selectedPrint);
    if (primary) series.push(primary);
    // Add up to one comparison series (different treatment with price)
    const comp = printsWithPrice.find(p => p.id !== selectedPrint.id);
    if (comp) {
      const s = make(comp);
      if (s) series.push(s);
    }
    return series;
  }, [selectedPrint, printsWithPrice, historyMap, range]);

  // Delta calc for headline
  const delta = useMemo(() => {
    if (!selectedPrint) return null;
    const hist = historyMap?.get(selectedPrint.id) || [];
    if (hist.length < 1 || selectedPrint.price_usd == null) return null;
    const prior = hist[hist.length - 1].price;
    const current = Number(selectedPrint.price_usd);
    const diff = current - prior;
    const pct = prior ? (diff / prior) * 100 : 0;
    return { diff, pct };
  }, [selectedPrint, historyMap]);

  const treatmentInfo = selectedPrint ? TREATMENT_LABELS[selectedPrint.treatment] : null;
  const trendUp = delta && delta.diff > 0;
  const trendDown = delta && delta.diff < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/85 backdrop-blur-sm" onClick={onClose}>
      <div
        className="animate-slide-up w-full max-w-[430px] max-h-[92vh] overflow-y-auto rounded-t-2xl border border-border bg-card"
        onClick={e => e.stopPropagation()}
      >
        {/* Hero */}
        <div className="relative px-5 pt-5 pb-3">
          <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-10 rounded-full bg-background/60 p-1.5 text-foreground backdrop-blur">
            <X className="h-4 w-4" />
          </button>
          <div className="flex justify-center">
            <CardImage
              src={selectedPrint?.image_url || card.image_url}
              alt={card.name}
              inkColor={card.ink_color as InkColor}
              enchanted={card.enchanted || false} rarity={card.rarity}
              showGlow
              className="w-44 aspect-[2/3]"
            />
          </div>
          <div className="mt-4">
            <h2 className="font-display text-xl font-semibold text-foreground leading-tight">
              {card.name}{card.subtitle ? ` — ${card.subtitle}` : ''}
            </h2>
            <p className="text-xs text-muted-foreground font-body mt-1">
              Lorcana · <span className="text-primary">{card.set_name}</span>
            </p>
            <p className="text-xs text-muted-foreground font-body">
              {card.rarity}{card.set_number ? ` · ${card.set_number}` : ''}
            </p>
          </div>

          {/* Price headline + treatment selector */}
          <div className="mt-4 flex items-start justify-between gap-3">
            <div className="relative">
              <button
                onClick={() => setTreatmentMenu(v => !v)}
                className="flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1.5 text-xs font-medium font-body text-foreground"
              >
                <span>{treatmentInfo?.emoji}</span>
                <span>{treatmentInfo?.label || 'Select'}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {treatmentMenu && printsWithPrice.length > 1 && (
                <div className="absolute z-20 mt-1 w-44 rounded-lg border border-border bg-popover p-1 shadow-lg">
                  {printsWithPrice.map(p => {
                    const i = TREATMENT_LABELS[p.treatment];
                    return (
                      <button
                        key={p.id}
                        onClick={() => { setSelectedPrintId(p.id); setTreatmentMenu(false); }}
                        className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-xs font-body hover:bg-secondary ${selectedPrint?.id === p.id ? 'text-primary' : 'text-foreground'}`}
                      >
                        <span className="flex items-center gap-1.5">{i?.emoji} {i?.label || p.treatment}</span>
                        <span className="text-muted-foreground">${Number(p.price_usd).toFixed(2)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="font-display text-2xl font-semibold text-foreground tabular-nums leading-none">
                {selectedPrint?.price_usd != null ? `$${Number(selectedPrint.price_usd).toFixed(2)}` : '—'}
              </p>
              {delta && (
                <p className={`mt-1 flex items-center justify-end gap-1 text-xs font-body tabular-nums ${trendUp ? 'text-ink-emerald' : trendDown ? 'text-ink-ruby' : 'text-muted-foreground'}`}>
                  {trendUp ? <TrendingUp className="h-3 w-3" /> : trendDown ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                  {delta.diff >= 0 ? '+' : ''}${delta.diff.toFixed(2)} ({delta.pct >= 0 ? '+' : ''}{delta.pct.toFixed(2)}%)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-5">
          <div className="flex rounded-full bg-secondary p-1">
            {(['raw', 'graded', 'pop'] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 rounded-full py-1.5 text-[11px] font-semibold uppercase tracking-wider font-body transition-colors ${tab === t ? 'bg-card text-foreground shadow' : 'text-muted-foreground'}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="px-5 pt-4 pb-3">
          {tab === 'raw' && (
            <>
              {chartSeries.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  {chartSeries.map(s => (
                    <div key={s.id} className="flex items-center gap-1.5">
                      <span className="inline-block h-1.5 w-3 rounded-full" style={{ background: s.color }} />
                      <span className="text-[10px] text-muted-foreground font-body">{s.label}</span>
                    </div>
                  ))}
                </div>
              )}
              <PriceChart series={chartSeries} />
              <div className="mt-3 flex justify-center gap-1">
                {RANGES.map(r => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    className={`min-w-[42px] rounded-full px-3 py-1 text-[11px] font-medium font-body transition-colors ${
                      range === r ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </>
          )}

          {tab === 'graded' && (
            <div className="rounded-xl border border-dashed border-border bg-card/40 p-6 text-center">
              <Award className="mx-auto h-8 w-8 text-primary mb-2" />
              <p className="text-sm font-medium text-foreground font-body">Graded sales coming soon</p>
              <p className="mt-1 text-xs text-muted-foreground font-body leading-relaxed">
                PSA & BGS comps require a premium data feed. Connect TCGplayer Pro, PSA, or GemRate to unlock graded pricing here.
              </p>
            </div>
          )}

          {tab === 'pop' && (
            <div className="space-y-3">
              <div className="rounded-xl border border-dashed border-border bg-card/40 p-4 text-center">
                <BarChart3 className="mx-auto h-7 w-7 text-primary mb-2" />
                <p className="text-sm font-medium text-foreground font-body">Population report coming soon</p>
                <p className="mt-1 text-xs text-muted-foreground font-body">
                  PSA / BGS census data requires a premium feed. Meanwhile, here are the known printings:
                </p>
              </div>
              <div className="rounded-xl border border-border bg-secondary p-3 space-y-1.5">
                {prints.map(p => {
                  const i = TREATMENT_LABELS[p.treatment] || { label: p.treatment, emoji: '🃏' };
                  return (
                    <div key={p.id} className="flex items-center justify-between text-xs font-body">
                      <span className="flex items-center gap-1.5 text-foreground">
                        {i.emoji} {i.label}
                        {p.rarity && <RarityBadge rarity={p.rarity} />}
                      </span>
                      <span className="text-primary tabular-nums">
                        {p.price_usd != null ? `$${Number(p.price_usd).toFixed(2)}` : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="px-5 pt-1">
          <div className="flex items-center gap-2 mb-2">
            <InkDot ink={card.ink_color as InkColor} size="sm" />
            <span className="text-xs font-medium text-foreground font-body">{card.ink_color}</span>
            <span className="text-xs text-muted-foreground font-body capitalize">· {card.card_type}</span>
            {card.inkable != null && (
              <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${card.inkable ? 'bg-ink-sapphire/20 text-ink-sapphire' : 'bg-ink-ruby/20 text-ink-ruby'}`}>
                {card.inkable ? 'Inkable' : 'Uninkable'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-1.5 text-xs font-body">
            {card.cost != null && <div className="rounded bg-secondary px-2 py-1 text-center"><span className="text-muted-foreground">Cost</span><br /><span className="text-foreground font-medium">{card.cost}</span></div>}
            {card.strength != null && <div className="rounded bg-secondary px-2 py-1 text-center"><span className="text-muted-foreground">STR</span><br /><span className="text-foreground font-medium">{card.strength}</span></div>}
            {card.willpower != null && <div className="rounded bg-secondary px-2 py-1 text-center"><span className="text-muted-foreground">WP</span><br /><span className="text-foreground font-medium">{card.willpower}</span></div>}
            {card.lore_value != null && <div className="rounded bg-secondary px-2 py-1 text-center"><span className="text-muted-foreground">Lore</span><br /><span className="text-foreground font-medium">{card.lore_value}</span></div>}
          </div>
        </div>

        {card.abilities && (
          <div className="px-5 pt-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1">Abilities</p>
            <p className="text-sm text-foreground font-body leading-relaxed">{card.abilities}</p>
          </div>
        )}
        {card.flavor_text && (
          <div className="px-5 pt-2">
            <p className="text-xs text-muted-foreground font-body italic leading-relaxed">{card.flavor_text}</p>
          </div>
        )}

        {/* Actions */}
        {isSignedIn && (
          <div className="sticky bottom-0 mt-4 flex gap-2 border-t border-border bg-card/95 backdrop-blur p-4">
            <button
              onClick={() => onAddToCollection(card)}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body"
            >
              <Plus className="h-4 w-4" />
              {owned ? `Add Another (${ownedQty})` : 'Add to Collection'}
            </button>
            <button
              onClick={() => onToggleWishlist(card)}
              className={`flex h-12 w-12 items-center justify-center rounded-lg border transition-colors ${
                wishlisted ? 'border-ink-ruby bg-ink-ruby/10 text-ink-ruby' : 'border-border bg-secondary text-muted-foreground'
              }`}
            >
              <Heart className={`h-5 w-5 ${wishlisted ? 'fill-current' : ''}`} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
